import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../../Services/Context/AuthContext";
import { useLanguage } from "../../Services/Context/LanguageContext";
import type { Phase } from "../../App/types";
import "./FirstVisitHints.css";

const HINTS_KEY = "fatespin_hints_v1";

interface FirstVisitHintsProps {
  phase: Phase;
}

export default function FirstVisitHints({ phase }: FirstVisitHintsProps) {
  const { user } = useAuth();
  const { translate } = useLanguage();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (phase !== "question") return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const scheduleShow = () => {
      if (cancelled) return;
      timer = setTimeout(() => {
        if (!cancelled) setShow(true);
      }, 1800);
    };

    // Fast local check — skip the API call for repeat visitors on the same device
    try {
      if (localStorage.getItem(HINTS_KEY)) return;
    } catch {}

    // IP-based check — catches visits from other devices / cleared storage
    fetch("/api/hints")
      .then((res) => (res.ok ? res.json() : { seen: false }))
      .then(({ seen }: { seen: boolean }) => {
        if (seen) {
          try {
            localStorage.setItem(HINTS_KEY, "1");
          } catch {}
          return;
        }
        scheduleShow();
      })
      .catch(() => scheduleShow());

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [phase]);

  // When hints become visible: mark as seen locally and on the server
  useEffect(() => {
    if (!show) return;
    try {
      localStorage.setItem(HINTS_KEY, "1");
    } catch {}
    fetch("/api/hints", { method: "POST" }).catch(() => {});
  }, [show]);

  // Auto-dismiss after 6 seconds
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => setShow(false), 9000);
    return () => clearTimeout(t);
  }, [show]);

  const dismiss = useCallback(() => setShow(false), []);

  const springIn = { type: "spring", stiffness: 260, damping: 22 } as const;
  const fadeOut = { duration: 0.25, ease: "easeIn" } as const;

  return (
    <AnimatePresence>
      {show && !user && (
        <motion.div
          key="hint-signin"
          className="FirstVisitHint FirstVisitHint--left"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12, transition: fadeOut }}
          transition={springIn}
          onClick={dismiss}
        >
          {translate("hintSignIn")}
        </motion.div>
      )}
      {show && (
        <motion.div
          key="hint-menu"
          className="FirstVisitHint FirstVisitHint--right"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12, transition: fadeOut }}
          transition={{ ...springIn, delay: 0.12 }}
          onClick={dismiss}
        >
          {translate("hintMenu")}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
